import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'react-feather';
import { COLORS } from '../../../../utils/colors';
import { DillButton, DillModal } from '../../../general/Dill';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { openDillToast } from '../../../../utils/helpers';
import {
  getExperianBusinessSearch,
  getExperianPremierProfile,
} from '../../../../redux/services/creditReportService';
import { ArrowsDownUp, SortAscending, SortDescending } from 'phosphor-react';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { getSupplierCreditApplication } from '../../../../redux/services/creditApplicationsService';
import StateDropDown from './StateDropDown';

type ExperianCompany = {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  confidence: string | number | null;
  tradelines: string | number | null;
};

const ExperianReportCompanyListModal = ({
  open,
  handleClose,
  data,
  onLearnMore,
  error,
  creditApplicationId,
  internalFormSubmissionId,
  onReportsChanged,
  companyDetails,
}: {
  open: boolean;
  handleClose: () => void;
  data: ExperianCompany[];
  onLearnMore: (data: any, reportId: string) => void;
  error: string;
  creditApplicationId: string;
  // On an internal form's review page: reports are kept on that submission, and
  // onReportsChanged reloads them instead of refetching a credit application.
  internalFormSubmissionId?: string;
  onReportsChanged?: () => void;
  companyDetails: {
    companyName: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState('');
  const [sortedData, setSortedData] = useState<ExperianCompany[]>([]);
  const [sort, setSort] = useState('');
  const [sorted, setSorted] = useState(false);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [queryText, setQueryText] = useState(companyDetails?.companyName ?? '');
  const [street, setStreet] = useState(companyDetails?.address ?? '');
  const [city, setCity] = useState(companyDetails?.city ?? '');
  const [stateInput, setStateInput] = useState(companyDetails?.state ?? '');
  const [zip, setZip] = useState(companyDetails?.zipCode ?? '');
  const [errorText, setErrorText] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [modifyCompany, setModifyCompany] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    companyName?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  }>({});

  const columns = useMemo(() => {
    return [
      { id: 'companyName', name: 'Company Name', width: 120, cellType: 'VIEW_TEXT' },
      { id: 'address', name: 'Address', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'city', name: 'City', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'state', name: 'State', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'zipCode', name: 'Zip Code', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'numberOfTradelines', name: 'Tradelines', width: 80, cellType: 'VIEW_TEXT' },
      { id: 'action', name: '', width: 140, cellType: 'VIEW_TEXT' },
    ];
  }, []);

  const totalWidth = useMemo(() => columns.reduce((curr, prev) => curr + prev.width, 0), [columns]);

  const handleCloseModal = () => {
    setLoading('');
    setQueryText('');
    setStreet('');
    setCity('');
    setStateInput('');
    setZip('');
    setSortedData([]);
    setModifyCompany(false);
    setFieldErrors({});
    handleClose();
  };

  useEffect(() => {
    if (!open) return;
    setQueryText(companyDetails?.companyName ?? '');
    setStreet(companyDetails?.address ?? '');
    setCity(companyDetails?.city ?? '');
    setStateInput(companyDetails?.state ?? '');
    setZip(companyDetails?.zipCode ?? '');
  }, [
    open,
    companyDetails?.companyName,
    companyDetails?.address,
    companyDetails?.city,
    companyDetails?.state,
    companyDetails?.zipCode,
  ]);

  useEffect(() => {
    setErrorText(error);
  }, [error]);

  useEffect(() => {
    if (!data) return;
    setSortedData([...data]);
  }, [data]);

  const sortByProperty = (
    sortType: 'asc' | 'desc',
    _sortKey: string,
    getter: (item: any) => any,
    compare?: (a: any, b: any) => number
  ) => {
    const filteredData = data.filter((company) => {
      const value = getter(company);
      return value !== undefined && value !== null;
    });

    const sortedDataCopy = [...filteredData];
    sortedDataCopy.sort((a, b) => {
      const valueA = getter(a);
      const valueB = getter(b);

      if (compare) {
        return compare(valueA, valueB) * (sortType === 'asc' ? 1 : -1);
      } else {
        const stringA = String(valueA).toUpperCase();
        const stringB = String(valueB).toUpperCase();
        if (stringA < stringB) return sortType === 'asc' ? -1 : 1;
        if (stringA > stringB) return sortType === 'asc' ? 1 : -1;
        return 0;
      }
    });

    const undefinedOrNull = data.filter((company) => {
      const value = getter(company);
      return value === undefined || value === null;
    });

    setSortedData(sortedDataCopy.concat(undefinedOrNull));
  };

  const handleSort = (value: string) => {
    if (!data) return;
    if (sort === value) {
      if (sorted) {
        setSortedData(data);
        setSort('');
        setSorted(false);
      } else {
        if (value === 'companyName') sortByProperty('desc', value, (c) => c.name);
        else if (value === 'address') sortByProperty('desc', value, (c) => c.addressLine1);
        else if (value === 'city') sortByProperty('desc', value, (c) => c.city);
        else if (value === 'state') sortByProperty('desc', value, (c) => c.state);
        else if (value === 'zipCode') sortByProperty('desc', value, (c) => c.zipCode);
        setSorted(true);
      }
    } else {
      if (value === 'companyName') sortByProperty('asc', value, (c) => c.name);
      else if (value === 'address') sortByProperty('asc', value, (c) => c.addressLine1);
      else if (value === 'city') sortByProperty('asc', value, (c) => c.city);
      else if (value === 'state') sortByProperty('asc', value, (c) => c.state);
      else if (value === 'zipCode') sortByProperty('asc', value, (c) => c.zipCode);
      setSort(value);
      setSorted(false);
    }
  };

  const validateSearchFields = () => {
    const errors: typeof fieldErrors = {};
    if (!queryText.trim()) errors.companyName = 'Company name is required';
    if (!street.trim()) errors.street = 'Street address is required';
    if (!city.trim()) errors.city = 'City is required';
    if (!stateInput.trim()) errors.state = 'State is required';
    if (!zip.trim()) errors.zip = 'Zip code is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSearch = async () => {
    if (!validateSearchFields()) return;
    setLoadingList(true);
    const result = await dispatch(
      getExperianBusinessSearch({
        supplierId: currentSupplier?.id ?? '',
        companyDetails: { companyName: queryText, street, city, state: stateInput, zip },
      })
    );
    if (result.type === 'creditReport/getExperianBusinessSearch/fulfilled') {
      const payload: any = result.payload;
      const payloadData = payload?.data.map((item: any) => ({
        id: item.experianBin,
        name: item.name,
        addressLine1: item.addressLine1 ?? '',
        addressLine2: item.addressLine2 ?? '',
        city: item.city ?? '',
        state: item.state ?? '',
        zipCode: item.zipCode ?? '',
        phone: item.phone ?? '',
        confidence: item.confidence ?? null,
        tradelines: item.tradelines ?? null,
      }));
      setSortedData(payloadData);
    } else {
      const payload: any = result.payload;
      const payloadErrorText =
        typeof payload === 'string' ? payload : 'Failed to get credit report.';
      setErrorText(payloadErrorText);
      setSortedData([]);
    }
    setLoadingList(false);
  };

  const handleViewReport = async (company: ExperianCompany) => {
    setLoading(company.id);
    const result = await dispatch(
      getExperianPremierProfile({
        supplierId: currentSupplier?.id ?? '',
        experianBin: company.id,
        name: company.name,
        type: 'business',
        creditApplicationId,
        internalFormSubmissionId,
        street: company.addressLine1,
        city: company.city,
        state: company.state,
        zip: company.zipCode,
        phone: company.phone,
      })
    );
    if (result.type === 'creditReport/getExperianPremierProfile/fulfilled') {
      const payload: any = result.payload;
      const payloadData = payload?.data;
      onLearnMore(payloadData, company.id);
      if (onReportsChanged) onReportsChanged();
      else
        dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier?.id || '',
            creditApplicationId,
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );
    } else {
      const payload: any = result.payload;
      openDillToast({
        message: typeof payload === 'string' ? payload : 'Failed to get Experian report.',
        type: 'ERROR',
      });
    }
    setLoading('');
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Experian Company List"
      maxWidth="80vw"
      footer={
        <div className="flex justify-end w-full">
          <DillButton label="Close" variant="secondary" onClick={handleCloseModal} />
        </div>
      }>
      <div className="flex flex-col gap-2">
        {sortedData.length === 0 && (
          <div className="w-full flex flex-col justify-center items-center p-4 gap-2">
            {!loadingList && (
              <div className="text-base ERROR_500-CLR font-semibold">{errorText}</div>
            )}
            {!loadingList && (
              <div className="text-sm GREY_500-CLR font-semibold">
                Please try again using a different search.
              </div>
            )}
            <div className="mt-4 w-1/2 gap-2 flex flex-col justify-center items-center">
              <AppInputField
                id="experianCompanyName"
                label="Company Name"
                value={queryText}
                placeholder="Enter Company Name"
                isRequired
                errorText={fieldErrors.companyName}
                onTextChange={(text) => setQueryText(text)}
              />
              <AppInputField
                id="experianStreet"
                label="Street Address"
                value={street}
                placeholder="Enter Street Address"
                isRequired
                errorText={fieldErrors.street}
                onTextChange={(text) => setStreet(text)}
              />
              <div className="w-full flex gap-2">
                <AppInputField
                  id="experianCity"
                  label="City"
                  value={city}
                  placeholder="City"
                  isRequired
                  errorText={fieldErrors.city}
                  onTextChange={(text) => setCity(text)}
                />
                <StateDropDown
                  label="State"
                  isRequired
                  errorText={fieldErrors.state}
                  value={stateInput}
                  onTextChange={(value) => setStateInput(value)}
                />
                <AppInputField
                  id="experianZip"
                  label="Zip Code"
                  value={zip}
                  placeholder="Zip Code"
                  isRequired
                  errorText={fieldErrors.zip}
                  onTextChange={(text) => setZip(text)}
                />
              </div>
              <DillButton
                label="Search"
                onClick={handleSearch}
                isLoading={loadingList}
                isDisabled={loadingList}
              />
            </div>
          </div>
        )}
        {sortedData.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex py-2 w-full">
              {modifyCompany && (
                <div className="flex flex-col w-full gap-2 p-3 rounded border GREY_200-BORDER GREY_50-BG">
                  <div className="text-sm font-semibold">Modify search</div>
                  <div className="flex gap-2 items-end flex-wrap">
                    <div className="flex-1 min-w-[160px]">
                      <AppInputField
                        id="experianCompanyName"
                        label="Company Name"
                        placeholder="Enter Company Name"
                        value={queryText}
                        isRequired
                        errorText={fieldErrors.companyName}
                        onTextChange={(text) => setQueryText(text)}
                      />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <AppInputField
                        id="experianStreet"
                        label="Street Address"
                        placeholder="Enter Street Address"
                        value={street}
                        isRequired
                        errorText={fieldErrors.street}
                        onTextChange={(text) => setStreet(text)}
                      />
                    </div>
                    <div className="flex-1 min-w-[120px]">
                      <AppInputField
                        id="experianCity"
                        label="City"
                        placeholder="City"
                        value={city}
                        isRequired
                        errorText={fieldErrors.city}
                        onTextChange={(text) => setCity(text)}
                      />
                    </div>
                    <div className="w-[150px]">
                      <StateDropDown
                        label="State"
                        isRequired
                        errorText={fieldErrors.state}
                        value={stateInput}
                        onTextChange={(value) => setStateInput(value)}
                      />
                    </div>
                    <div className="w-[100px]">
                      <AppInputField
                        id="experianZip"
                        label="Zip Code"
                        placeholder="Zip Code"
                        value={zip}
                        isRequired
                        errorText={fieldErrors.zip}
                        onTextChange={(text) => setZip(text)}
                      />
                    </div>
                    <DillButton
                      label="Search"
                      onClick={handleSearch}
                      isLoading={loadingList}
                      isDisabled={loadingList}
                      height={40}
                    />
                    {!loadingList && (
                      <DillButton
                        variant="ghost"
                        onClick={() => setModifyCompany(false)}
                        icon={<X size={18} color={COLORS.ERROR_500} />}
                        width={40}
                        height={40}
                      />
                    )}
                  </div>
                </div>
              )}
              {!modifyCompany && (
                <DillButton
                  label="Modify search"
                  variant="secondary"
                  onClick={() => setModifyCompany(true)}
                  isLoading={loadingList}
                  isDisabled={loadingList}
                  height={35}
                />
              )}
            </div>
            <div className="border rounded">
              <div className="w-full flex border-b justify-between px-4 sticky top-0 PRIMARY_50-BG items-center z-1">
                {columns.map((column, i) => (
                  <div
                    key={column.id + '-experian-header-' + i}
                    style={{ flex: column.width / totalWidth }}
                    className={twMerge('flex py-3 mx-0.5 cursor-pointer items-center')}
                    onClick={() => handleSort(column.id)}>
                    <p className="text-xs TEXT_SECONDARY-CLR">{column.name}</p>
                    {column.id !== 'action' && (
                      <div>
                        {sort === column.id && sorted ? (
                          <SortAscending color={COLORS.GREY_400} />
                        ) : sort === column.id && !sorted ? (
                          <SortDescending color={COLORS.GREY_400} />
                        ) : (
                          <ArrowsDownUp color={COLORS.GREY_400} />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="max-h-[500px] overflow-scroll">
                {sortedData.map((company, index) => {
                  const row = {
                    companyName: company.name,
                    address: [company.addressLine1, company.addressLine2]
                      .filter(Boolean)
                      .join(', '),
                    city: company.city,
                    state: company.state,
                    zipCode: company.zipCode,
                    numberOfTradelines: company.tradelines ?? '—',
                  };

                  return (
                    <div
                      key={index + 'experian-' + company.id}
                      className="w-full flex items-center px-4 justify-between border-b border-b-gray-200">
                      {columns.map((column, i) => (
                        <div
                          key={column.id}
                          style={{ flex: column.width / totalWidth }}
                          className={`flex py-3 mx-0.5 ${
                            columns.length - 1 !== i ? 'overflow-hidden' : ''
                          }`}>
                          {column.id !== 'action' && (
                            <p className="capitalize w-full text-xs">
                              {row[column.id as keyof typeof row]}
                            </p>
                          )}
                          {column.id === 'action' && (
                            <DillButton
                              label="View Report"
                              variant="secondary"
                              onClick={() => handleViewReport(company)}
                              isLoading={loading === company.id}
                              isDisabled={loading !== ''}
                              height={35}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default ExperianReportCompanyListModal;
